/**
 * `case-files-view` — the pure view model behind the consolidated case-files
 * section ("Acervo" in pt-BR / "The File" in en-US).
 *
 * Plan: `ai-docs/lexide-acervo-consolidated-view-plan.md` §6 F1. This file is
 * the F1 half: a pure function over the tree payload the SPA already fetches
 * (`useCaseWorkspaceTree`), with NO new fetch, NO store, and no React — the
 * `useWorkspaceAliasMap` precedent. Everything here is unit-testable against
 * plain literals.
 *
 * ## The rules that are load-bearing (each cost something to learn)
 *
 * 1. **Grouping is PURE MODALITY** (D4, operator-ruled 2026-07-31). The
 *    arrival channel is complementary information rendered on the expanded
 *    row — never a grouping key. An earlier draft grouped WhatsApp/e-mail by
 *    channel; that was overruled.
 *
 * 2. **The modality vocabulary is OPEN.** A value this file does not know
 *    renders as its own passthrough group with the raw token as its label,
 *    never dropped and never folded into the obsolete state. This is not
 *    defensive: a live probe (2026-07-31) found `whatsapp` in service beyond
 *    the six `UploadRoute.modality` upload routes, so the passthrough is
 *    already carrying real files.
 *
 * 3. **Obsolete is a CONJUNCTION**, never one missing field: an INGEST zone
 *    AND `modality` absent AND the envelope's `ingestMeta` marker present.
 *    Drop any one and the ⚠ becomes a false warning on a healthy file — a
 *    runtime that predates the field serves it for NOBODY, which is what the
 *    marker exists to distinguish.
 *
 * 4. **There is no separate "Originais" node** (operator 2026-07-31, reversing
 *    D2). The processed entry already POINTS AT its original — the card offers
 *    "Baixar original" under the uploader's own filename and the viewer reads
 *    through to it — so a second folder holding the same material was
 *    redundant. `raw-intake/` is therefore not rendered at all.
 *
 * 5. **There is NO "Pendente" group** (§6 F1 step 5). The rule that would
 *    have built it — a raw-intake file whose `sourceId` has no `sources/`
 *    twin — was FALSIFIED against the live fleet: all 15 unmatched files on
 *    the probe case were `.zip`/`.eml`/`.rar` CONTAINERS, expanded at ingest
 *    into children carrying their own hashes, so the archive never matches
 *    and would read "awaiting processing" forever. The defect is the shape,
 *    not the extensions: it infers a lawyer-facing claim from a NEGATIVE, and
 *    "no match" covers processed-into-children, still-queued and died-early
 *    alike. A positive signal is needed before the group can exist; excluding
 *    container extensions client-side is rejected (it is a client-side copy of
 *    the server's route table).
 */

import type { WorkspaceTreeFile, WorkspaceTreeFolder } from "@/types/gateway";

/** On-disk zone names this view consumes. Everything else stays on the
 *  classic tree path, which is why an agent-authored artifact or draft can
 *  never reach the obsolete state. */
export const SOURCES_ZONE = "sources";
export const RAW_INTAKE_ZONE = "raw-intake";

/** The GENERATED-tier roots (guide §2 tier table: `plans/` · `analyses/` ·
 *  `output/`). Gates the A-14 `Detalhes` pane (always-shown provenance +
 *  user-notes for machine-authored documents). ⚠ This is the R2-pinned,
 *  CONTRACT-frozen ten-root set — not an open vocabulary — which is the only
 *  reason a client-side list is legitimate here (the same standing the
 *  `SOURCES_ZONE` constant above already has). */
export const GENERATED_ZONES: ReadonlySet<string> = new Set(["plans", "analyses", "output"]);

/** Is this workspace-relative path inside a Generated-tier root? Exact first
 *  segment — `analyses/x.md` yes, `analyses-old/x.md` no. */
export function isGeneratedZonePath(path: string): boolean {
  const slash = path.indexOf("/");
  if (slash <= 0) return false;
  return GENERATED_ZONES.has(path.slice(0, slash));
}

/**
 * Folders named after an ARRIVAL CHANNEL (the old channel-drop layout).
 *
 * ⚠ **These are no longer ROOTS.** Under the pinned shape
 * (`ai-docs/case-workspace-guide.md` §1) the root set is exactly seven, and
 * `email`/`whatsapp` are **SUBZONES** — `sources/email/`, `sources/whatsapp/`
 * (§1.2), mirrored as verbatim landing zones under `raw-intake/`. They are
 * chosen by DELIVERY CHANNEL, never by content, and they are permanent.
 * This set is kept as a root-level filter for legacy workspaces that still
 * carry a channel-named root until the reset sweeps them; on a pinned case it
 * matches nothing. It is also why `caseFolders.email` / `caseFolders.whatsapp`
 * SURVIVE the legacy-key deletion — they are the only labels the SPA has for
 * the subzones, and `CaseFileNav` resolves the folder map by NAME AT ANY DEPTH.
 *
 * D4 ruled pure modality, and the operator ruled these folders off the tree
 * entirely (2026-07-31): a channel is **provenance on the item**, not a place.
 * Showing `E-MAIL` and `WHATSAPP` as folders re-creates the very split this
 * view exists to remove — the same content filed by how it ARRIVED rather than
 * by what it IS.
 *
 * Keyed on the SERVER's `upload_source` vocabulary, so the two cannot drift:
 * the same tokens the card renders through `caseFiles.channels.*`. A channel
 * this list does not know stays VISIBLE rather than hidden — the safe
 * direction, since an unexpected folder is a question a lawyer can ask, while
 * a silently hidden one is content they never learn exists.
 */
export const CHANNEL_ZONE_NAMES: ReadonlySet<string> = new Set([
  "email",
  "whatsapp",
  "telegram",
  "lexide",
  "agent",
  "api",
  "migration",
]);

/**
 * The DISPLAY group key for a modality, or null when the vocabulary does not
 * know it. Exported so any surface that needs to LABEL a modality goes through
 * the same mapping the grouping uses — a second copy would drift, and the raw
 * token (`office`, `doc`) is server vocabulary a lawyer should never read.
 */
export function displayGroupKeyForModality(modality: string | undefined): string | null {
  const m = modality?.trim();
  return m ? (MODALITY_GROUP[m] ?? null) : null;
}

/** Is this root folder one the consolidated view REPLACES? */
export function isCaseFilesOwnedZone(name: string): boolean {
  return name === SOURCES_ZONE || name === RAW_INTAKE_ZONE || CHANNEL_ZONE_NAMES.has(name);
}

/**
 * Server modality → display group key.
 *
 * Keyed on the SERVER's vocabulary, NEVER on a diagram's display labels — an
 * earlier draft of the plan invented keys (`spreadsheets`…) by reading §4's
 * Portuguese labels back into the wire vocabulary, which would have sent every
 * file to a passthrough group.
 *
 * `pdf` and `doc` deliberately MERGE into one "documents" group (as §4 shows);
 * `office` keeps its own because it spans spreadsheets AND presentations, so
 * it cannot be labelled "Planilhas" without misfiling a `.pptx`.
 */
const MODALITY_GROUP: Readonly<Record<string, string>> = {
  pdf: "documents",
  doc: "documents",
  image: "images",
  audio: "audio",
  video: "video",
  office: "office",
  whatsapp: "whatsapp",
};

/** Fixed render order for KNOWN groups (§4). Passthrough groups sort
 *  alphabetically after these; `stale` is always last. */
const GROUP_ORDER: readonly string[] = [
  "documents",
  "images",
  "audio",
  "video",
  "office",
  "whatsapp",
];

/** The obsolete (⚠ Reprocessar) bucket. */
export const STALE_GROUP = "stale";

/** Prefix for an unrecognised-but-present modality (rule 2). */
export const PASSTHROUGH_PREFIX = "mod:";

export type CaseFilesGroupKind =
  | "modality"
  | "unknown-modality"
  | "stale"
  /** RESERVED — never emitted today; see rule 4. */
  | "pending";

export interface CaseFilesGroup {
  /** Stable EN key — `documents` | `images` | … | `mod:<value>` | `stale`.
   *  Labels come from i18n keyed on this, never from the key itself. */
  key: string;
  kind: CaseFilesGroupKind;
  /** For a passthrough group, the raw server token to render verbatim.
   *  Absent on known groups (their label comes from i18n). */
  rawModality?: string;
  files: WorkspaceTreeFile[];
}

/** The ORIGINAL's provenance, resolved for one `sources/*` entry. */
export interface ResolvedOrigin {
  /** Arrival channel of the ORIGINAL upload (`lexide` → shown as "app"). */
  channel?: string;
  /** When the ORIGINAL arrived (ISO-8601). */
  uploadedAt?: string;
}

export interface CaseFilesView {
  /** Fixed order, EMPTY groups omitted (including `stale`). */
  groups: CaseFilesGroup[];
  /** Root-node count = the canonical `sources/*` entries. */
  count: number;
  /**
   * `sources/*` path → the ORIGINAL's provenance.
   *
   * ⚠ Why this join exists at all: a `sources/*` entry's own `channel` records
   * the writer of that entry — the intake harness — so it reads `agent` for
   * essentially everything (measured: 95/95 on the dev slice case). Rendering
   * it directly would tell a lawyer every document came from "the assistant",
   * which is uniform noise that HIDES the real origin. The arrival channel the
   * lawyer cares about lives on the raw-intake twin, reachable by `sourceId` —
   * a real join over data already on the wire, not an inference. It recovers
   * the true origin for 75/95 on the same case (lexide 44 · email 22 ·
   * telegram 9). Absent → the card renders nothing rather than guessing.
   */
  originByPath: ReadonlyMap<string, ResolvedOrigin>;
}

/**
 * Defensive belt-and-suspenders filter, mirrored from the canonical runtime
 * rule (`isSidecarPath`). The server already drops these; a stale or future
 * runtime must never leak one into a lawyer's tree.
 *
 * Canonical source: `lx-kernel/src/storage/blob-metadata-sidecar.ts`
 * (`SIDECAR_SUFFIX = ".lexmeta.json"`).
 */
export function isUserFacingFile(file: { name: string }): boolean {
  return !file.name.endsWith(".lexmeta.json");
}

/** Depth-first flatten of a folder's user-facing files. The virtual groups
 *  REPLACE the physical subfolder nesting, so nothing here preserves it. */
function flattenFiles(folder: WorkspaceTreeFolder): WorkspaceTreeFile[] {
  const out: WorkspaceTreeFile[] = [];
  const walk = (f: WorkspaceTreeFolder) => {
    for (const file of f.files) if (isUserFacingFile(file)) out.push(file);
    for (const sub of f.folders) walk(sub);
  };
  walk(folder);
  return out;
}

/**
 * Which group does this file belong to?
 *
 * `ingestMetaServed` is the envelope marker. When it is FALSE the caller
 * should not be rendering this view at all (§6.0 gates the whole section on
 * it) — but the parameter is threaded here anyway so the conjunction in rule 3
 * is expressed in ONE place rather than split across the model and its caller.
 */
function groupKeyFor(
  file: WorkspaceTreeFile,
  ingestMetaServed: boolean,
): { key: string; kind: CaseFilesGroupKind; rawModality?: string } {
  const modality = file.modality?.trim();
  if (!modality) {
    // Rule 3's conjunction. Without the marker we cannot tell "this file is
    // stale" from "this runtime serves the field for nobody", so the honest
    // answer is the passthrough-less unknown bucket the caller suppresses —
    // never a ⚠ on a healthy file.
    return { key: STALE_GROUP, kind: ingestMetaServed ? "stale" : "pending" };
  }
  const known = MODALITY_GROUP[modality];
  if (known) return { key: known, kind: "modality" };
  return {
    key: `${PASSTHROUGH_PREFIX}${modality}`,
    kind: "unknown-modality",
    rawModality: modality,
  };
}

/** Stable ordering: known groups in §4 order, then passthroughs alphabetically
 *  by their raw token, then `stale` last. */
function compareGroups(a: CaseFilesGroup, b: CaseFilesGroup): number {
  const rank = (g: CaseFilesGroup) => {
    if (g.key === STALE_GROUP) return 2;
    return g.kind === "unknown-modality" ? 1 : 0;
  };
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;
  if (ra === 0) return GROUP_ORDER.indexOf(a.key) - GROUP_ORDER.indexOf(b.key);
  return a.key.localeCompare(b.key);
}

/**
 * Build the view model.
 *
 * @param sources the `sources/` folder from the tree, or null when absent
 * @param ingestMetaServed the envelope's `ingestMeta` marker (see rule 3)
 */
export function buildCaseFilesView(
  sources: WorkspaceTreeFolder | null,
  /**
   * The `raw-intake/` folder — used ONLY to resolve each source's original
   * provenance (see `originByPath`). It is deliberately NOT rendered: the
   * "Originais" node was removed 2026-07-31 because the processed entry
   * already points at its original.
   */
  rawIntake: WorkspaceTreeFolder | null,
  ingestMetaServed: boolean,
): CaseFilesView {
  const files = sources ? flattenFiles(sources) : [];

  // sourceId → the ORIGINAL's provenance. First writer wins: a hash can be
  // shared by several raw entries (duplicate uploads of identical bytes), and
  // they carry the same origin by construction.
  const originBySourceId = new Map<string, ResolvedOrigin>();
  if (rawIntake) {
    for (const raw of flattenFiles(rawIntake)) {
      if (!raw.sourceId || originBySourceId.has(raw.sourceId)) continue;
      if (!raw.channel && !raw.uploadedAt) continue;
      originBySourceId.set(raw.sourceId, {
        channel: raw.channel,
        uploadedAt: raw.uploadedAt,
      });
    }
  }
  const originByPath = new Map<string, ResolvedOrigin>();
  for (const f of files) {
    const twin = f.sourceId ? originBySourceId.get(f.sourceId) : undefined;
    // Prefer the ORIGINAL's arrival over this entry's own writer stamp; fall
    // back to the entry's own `uploadedAt` when there is no twin.
    const channel = twin?.channel;
    const uploadedAt = twin?.uploadedAt ?? f.uploadedAt;
    if (channel || uploadedAt) originByPath.set(f.path, { channel, uploadedAt });
  }
  const byKey = new Map<string, CaseFilesGroup>();

  for (const file of files) {
    const { key, kind, rawModality } = groupKeyFor(file, ingestMetaServed);
    // A file whose modality is absent on a pre-marker runtime is NOT rendered
    // as obsolete — it simply carries no grouping information, and the caller
    // is showing the classic tree anyway.
    if (kind === "pending") continue;
    let group = byKey.get(key);
    if (!group) {
      group = { key, kind, rawModality, files: [] };
      byKey.set(key, group);
    }
    group.files.push(file);
  }

  return {
    // EMPTY groups are omitted by construction (a group only exists once a
    // file lands in it) — including ⚠ Reprocessar, so a permanently-visible
    // empty warning group can never read as standing anxiety.
    groups: [...byKey.values()].sort(compareGroups),
    count: files.length,
    originByPath,
  };
}
